import {
  apiUrl as channelApiUrl,
  previewProjectUrl,
  mermaidUrl,
  normalizeProjectKey,
  currentChannel
} from '@symbo.ls/channels'
import { currentChannelFromHost } from './canvasUrl.js'
import { resolveProjectParams } from './resolveProjectParams.js'

export function generatePreviewUrl() {
  // Bug #2051: the previous implementation built `https://${appKey}` for
  // production and `${channelApiUrl}/?renderUrl=${appKey}` for everything
  // else. Neither URL points at the canonical preview host
  // (`{owner}--{key}.<tier>.preview.symbols.app`), so the "Open in new
  // tab" button opened a non-existent host that the browser couldn't
  // resolve. Route through `previewProjectUrl` from @symbo.ls/channels —
  // the same builder used by the CLI publish summary and the studios
  // project tile — so the URL is always the published-preview host.

  // FA204 / frank trap — moved from module scope into this function (sole
  // consumer, pure helper — see longPress.js for the identical shape).
  // Resolve the active project's owner/key from root state. Prefers the
  // canonical canvas pathname pair (`workingProjectOwner`/`workingProjectKey`)
  // that the studios router sets, falling back to legacy state shapes used
  // by older canvas modes (raw `owner` username + `appKey`).
  function _resolveProjectIdentity(root) {
    const s = root || {}
    let owner =
      s.workingProjectOwner ||
      s.owner?.username ||
      (Array.isArray(s.members) ? s.members.find((m) => m.role === 'owner')?.username : null) ||
      null
    let rawKey = s.workingProjectKey || s.appKey || s.key || null
    // Canvas v2 project route: none of the legacy state shapes above are set —
    // root carries only `data` — so "Open in new tab" resolved {null, null} and
    // generatePreviewUrl returned null, a silent dead click (live-verified
    // 2026-08-08 per Nika "icons clicking and functionality"). The /:owner/:key
    // route itself is the canonical identity there; resolveProjectParams is the
    // same shared helper the canvas boot uses.
    if (!owner || !rawKey) {
      const fromRoute = resolveProjectParams()
      if (!owner && fromRoute.owner) owner = fromRoute.owner
      if (!rawKey && fromRoute.key) rawKey = fromRoute.key
    }
    const key = rawKey ? normalizeProjectKey(rawKey, { owner }) : null
    return { owner: owner ? String(owner).toLowerCase() : null, key }
  }

  const root = this.getRootState() || {}
  const { owner, key } = _resolveProjectIdentity(root)
  if (!owner || !key) return null

  const env = root.ENV || this.getRootState?.('ENV') || 'production'

  // Selected canvas item: append `/component?key=X` or `/<pagePath>` so
  // the preview app deep-links into the right surface.
  const isCanvas = this.call('isCanvas')
  const selected = isCanvas && this.getSelected()
  if (selected?.state?.content?.src) {
    return selected.state.content.src
  }

  const selectedKey = isCanvas && this.getSelectedKey()
  const version = this.getActiveVersion?.()
  const latestVersion = this.getLatestVersion?.()
  const isOld = version && version !== latestVersion

  let base = previewProjectUrl({ owner, key, env })
  if (!base) {
    // Fallback to the rendered API path only when the channel has no
    // preview host configured (local channel without portless mapping).
    const target =
      key +
      (selectedKey
        ? selectedKey.startsWith('/')
          ? selectedKey
          : `/component?key=${selectedKey}`
        : '')
    return `${channelApiUrl(env)}/?renderUrl=${encodeURIComponent(target)}`
  }

  // `previewProjectUrl` returns a URL ending in `/` (no path), with an
  // optional `?env=…` query when env !== production. Splice the selected
  // path/query INTO the URL preserving the env query.
  const url = new URL(base)
  if (selectedKey) {
    url.pathname = selectedKey.startsWith('/') ? selectedKey : '/component'
    if (!selectedKey.startsWith('/')) url.searchParams.set('key', selectedKey)
  }
  if (isOld) url.searchParams.set('version', version)
  return url.toString()
}

export function generateUrl() {
  const selected = this.getSelected()
  const appKey = this.getAppKey()
  if (!selected) {
    return `${appKey.split('.')[0]}`
  }
  const key = this.getSelectedKey()
  const isPage = key.startsWith('/')
  const path = isPage ? key : `/${key}`
  return `symbo.ls/${appKey.split('.')[0]}${path || ''}`
}

export function openInNewTab() {
  const openUrl = this.call('generatePreviewUrl')
  const url = window.open(openUrl, 'Symbols preview')

  // Setup interval to check when the window is closed
  this.syncCanvas({ openUrl })

  const checkInterval = setInterval(() => {
    if (url && url.closed) {
      this.syncCanvas({ openUrl: null })
      clearInterval(checkInterval)
    }
  }, 1500)
}

// Build the canonical project URL bundle for any (owner, key, env) triple.
//
// Previously lived in `canvas/functions/buildProjectUrls.js` with its own
// `normalizeProjectKey` impl. Consolidated here so canvas + studios use the
// same source as the rest of shared/, with helpers routed through
// `@symbo.ls/channels` (single source of truth across server, CLI,
// workspace, marketplace).
//
// Returns `null` when either owner or key normalizes to empty — lets
// callers disable / omit links instead of generating malformed URLs.
export function buildProjectUrls(owner, key, env = 'development') {
  const safeOwner = String(owner || '')
    .trim()
    .toLowerCase()
  const safeKey = normalizeProjectKey(key, { owner: safeOwner })
  if (!safeOwner || !safeKey) return null

  // Preview stays cross-domain (preview hosts on `*.preview.symbols.app`,
  // a separate origin from the workspace shell). Canvas is mounted at the
  // same origin as the workspace shell via the unified multi-app entry,
  // so the canvas URL is a relative path the SPA router catches without a
  // host hop.
  const channel = currentChannelFromHost() || currentChannel() || undefined
  // "Open directly" / "Direct (dev|prod)" always open the LIVE deployed app
  // on the canonical mermaid host (`owner--key[--env].at.symbo.ls`), NOT the
  // current host's channel — on workspace.localhost the host channel is
  // 'local', which would resolve mermaidUrl to `…localhost:1355` (the local
  // dev server) instead of the deployed project. The env still rides the
  // `--env` subdomain segment; only the domain is pinned to production.
  // (2026-06-14 per Nika: "Open Directly should open *.at.symbo.ls urls".)
  const directChannel = 'production'
  return {
    safeOwner,
    safeKey,
    preview: previewProjectUrl({ owner: safeOwner, key: safeKey, env }, channel),
    canvas: `/${safeOwner}/${safeKey}?env=${env}`,
    directDev: mermaidUrl({ owner: safeOwner, key: safeKey, env: 'development' }, directChannel),
    directStaging: mermaidUrl({ owner: safeOwner, key: safeKey, env: 'staging' }, directChannel),
    directProd: mermaidUrl({ owner: safeOwner, key: safeKey, env: 'production' }, directChannel)
  }
}
